import React, { useState } from "react";
import SafeImage from "../components/SafeImage";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiArrowLeft,
  FiCheck,
  FiTag,
  FiLayers,
  FiPackage,
  FiMessageCircle,
} from "react-icons/fi";
import "./DetailPage.css";


const WA_NUMBER = "923420757213";

function waLink(productName) {
  const msg = encodeURIComponent(
    "Hi Shaheen Sports! I'm interested in ordering: " +
    productName +
    ". Please share pricing and availability."
  );
  return `https://wa.me/${WA_NUMBER}?text=${msg}`;
}

export default function ProductListing({
  catMeta,
  products,
  backPath,
  backLabel,
}) {
  const [openId, setOpenId] = useState(null);
  const [imgIndex, setImgIndex] = useState({});

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  const nextImg = (id, images) => {
    setImgIndex((prev) => ({
      ...prev,
      [id]: ((prev[id] || 0) + 1) % images.length,
    }));
  };

  const prevImg = (id, images) => {
    setImgIndex((prev) => ({
      ...prev,
      [id]:
        ((prev[id] || 0) - 1 + images.length) % images.length,
    }));
  };

  return (
    <div className="detail-page">
      {/* HERO */}
      <section className="detail-hero">
        <div
          className="detail-hero__bg"
          style={{ backgroundImage: `url(${catMeta.img})` }}
        />
        <div className="detail-hero__overlay" />
        <div className="container detail-hero__content">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            {backPath && backLabel && (
              <>
                <Link to={backPath}>{backLabel}</Link>
                <span>/</span>
              </>
            )}
            <span>{catMeta.title}</span>
          </div>

          <h1 className="detail-hero__title">{catMeta.title}</h1>
          <p className="detail-hero__tagline">{catMeta.tagline}</p>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="detail-products">
        <div className="container">
          <div className="detail-products__header">
            <div>
              <span className="section-label">{catMeta.title}</span>
              <h2 className="section-title">
                {products.length} Products Available
              </h2>
              <div className="divider-gold" />
            </div>

            {backPath && backLabel && (
              <Link to={backPath} className="back-link">
                <FiArrowLeft size={16} /> Back to {backLabel}
              </Link>
            )}
          </div>

          <div className="dp-grid">
            {products.map((product) => {
              const images =
                product.images && product.images.length > 0
                  ? product.images
                  : [product.img];

              const current = imgIndex[product.id] || 0;

              return (
                <div
                  key={product.id}
                  className={`dp-card ${openId === product.id ? "dp-card--active" : ""
                    }`}
                >
                  {/* IMAGE SLIDER */}
                  <div className="dp-card__img-wrap">

                    <SafeImage
                      src={images[current]}
                      alt={product.name}
                      className="dp-card__img"
                    />

                    {images.length > 1 && (
                      <div className="img-nav">
                        <button
                          className="img-btn"
                          onClick={() => prevImg(product.id, images)}
                        >
                          ‹
                        </button>

                        <button
                          className="img-btn"
                          onClick={() => nextImg(product.id, images)}
                        >
                          ›
                        </button>
                      </div>
                    )}

                    <span className="dp-card__badge">{product.tag}</span>

                  </div>

                  {/* BODY */}
                  <div className="dp-card__body">
                    <h3 className="dp-card__name">{product.name}</h3>

                    <div className="dp-card__specs">
                      <div className="dp-card__spec">
                        <FiLayers size={13} />
                        <span>
                          <strong>Material:</strong> {product.fabric}
                        </span>
                      </div>

                      <div className="dp-card__spec">
                        <FiPackage size={13} />
                        <span>
                          <strong>Best For:</strong> {product.use}
                        </span>
                      </div>

                      <div className="dp-card__spec">
                        <FiTag size={13} />
                        <span>
                          <strong>MOQ:</strong> {product.moq}
                        </span>
                      </div>
                    </div>

                    <p className="dp-card__desc">{product.desc}</p>

                    {/* COLORS */}
                    {product.colors && (
                      <div className="dp-card__colors">
                        <span className="dp-card__colors-label">
                          Available Colors:
                        </span>
                        <div className="dp-card__color-list">
                          {product.colors.map((c) => (
                            <span key={c} className="dp-card__color-chip">
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* DETAILS */}
                    <div
                      className={`dp-card__details ${openId === product.id
                          ? "dp-card__details--open"
                          : ""
                        }`}
                    >
                      <h4>Specifications:</h4>
                      <ul>
                        {product.details.map((d) => (
                          <li key={d}>
                            <FiCheck size={13} color="var(--gold)" />
                            {d}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* ACTIONS */}
                    <div className="dp-card__actions">
                      <button
                        className="dp-card__toggle"
                        onClick={() => toggle(product.id)}
                      >
                        {openId === product.id
                          ? "Hide Details ↑"
                          : "View Details ↓"}
                      </button>

                      <a
                        href={waLink(product.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dp-card__wa-btn"
                      >
                        <FiMessageCircle size={15} />
                        Order on WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="detail-cta">
        <div className="container detail-cta__inner">
          <div>
            <h2>Need a Custom Order?</h2>
            <p>
              Custom colors, branding, GSM and packaging — we handle everything
              in-house.
            </p>
          </div>

          <div className="detail-cta__btns">
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
                "Hi! I need a custom quote for " + catMeta.title
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wa"
            >
              <FiMessageCircle size={18} />
              WhatsApp Us
            </a>

            <Link to="/contact" className="btn-primary">
              Send Inquiry <FiArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}